import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import { errorCodes } from "@Helpers/errorCodes";
import type { ApiRoutes } from "@ServerTypes/apiContract";

// ============================================================================
// Shared Types
// ============================================================================

export interface PlatformAdapter {
  storage: {
    getItem: (key: string) => Promise<string | null> | string | null;
    removeItem: (key: string) => Promise<void> | void;
  };
  navigation: {
    getCurrentPath: () => string;
    redirectToLogin: (params?: { redirectUrl?: string | undefined; errorMessage?: string | undefined }) => void;
  };
  dispatch: (action: unknown) => void;
  actions: {
    setLoggedOut: () => unknown;
    setLicenceInvalid?: () => unknown;
    // Takes the failure body, not a string: resolving an errorCode to text needs i18n, which is a
    // platform concern. Keeping it here is what lets this file stay free of any i18n import.
    showToast?: (body: { status: false; errorCode: number; params?: Record<string, string | number> }) => unknown;
  };
  getRequestHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  // Whether this platform authenticates via browser cookies (default true). Native
  // mobile authenticates with a Bearer header and must set this to false: attaching
  // cookies makes the OS cookie jar replay the persistent jwtToken cookie, which
  // switches the server's CSRF enforcement on for requests that can't satisfy it
  // (the csrfToken session cookie does not survive an app restart).
  useCookieAuth?: boolean;
  excludedPathsFor401?: string[];
  // Resolves the backend origin (e.g. `${window.location.origin}/api-server`). Required so
  // internal requests like the CSRF token fetch hit the backend instead of the frontend host.
  getBackendUrl: () => string;
  // Whether the user has an authenticated session. Used only to suppress the
  // csrf-missing warning on pre-login requests the server never CSRF-checks.
  isAuthenticated?: () => boolean;
  // Told whether each finished request reached the backend: true for any answer the app itself gave
  // (including 4xx/500), false for a transport failure, timeout or infra status. Web uses it to decide
  // between the "we'll be right back" and "you're offline" screens. Cancellations report nothing.
  onBackendReachability?: (reachable: boolean) => void;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

type UnspecifiedResponseType = {
  readonly __brand: "ERROR: You must specify a response type — use axiosWrapper.post<YourResponseType>(...)";
};

type NoAnyGuard<T> = 0 extends 1 & T ? [never] : [];

/** An HTTP method a server route contract can declare (packages/server/helpers/routeContract.ts). */
export type ApiMethod = keyof ApiRoutes;
/** A route `api.<method>` can call: one declared in a server contract (packages/server/apiContract.ts), by its full path. */
export type ApiPath<M extends ApiMethod> = keyof ApiRoutes[M] & string;
/** What a route takes: the input side of its registered request schema — `unknown` where it declares none. */
export type ApiRequest<M extends ApiMethod, P extends ApiPath<M>> = ApiRoutes[M][P] extends { request: infer R }
  ? R
  : never;
/** What a route answers with: the body its registered `responseSchema` declares. */
export type ApiResult<M extends ApiMethod, P extends ApiPath<M>> = ApiRoutes[M][P] extends { response: infer R }
  ? R
  : never;

/** A POST route `api.post` can call with no `:name` segment to fill — the paths `postTo` takes. */
export type ApiPostPath = PlainPath<"post">;
/** The body `api.post(path, …)` sends: the input side of that route's registered request schema. */
export type ApiPostRequest<P extends ApiPostPath> = ApiRequest<"post", P>;
/** What `api.post(path, …)` resolves with: the body that route's registered `responseSchema` declares. */
export type ApiPostResponse<P extends ApiPostPath> = ApiResult<"post", P>;

/**
 * A route's success body: the `status: true` arm of the response its contract declares. Name a route's data with
 * this (keyed by the path the code calls) instead of restating `Extract<XResponse, { status: true }>` against a
 * server type. `never` for a route whose response has no `status: true` arm.
 */
export type ApiSuccessOf<M extends ApiMethod, P extends ApiPath<M>> = Extract<ApiResult<M, P>, { status: true }>;
/** One element of a list route's success `result` (`never` when `result` is not a list). */
export type ApiRowOf<M extends ApiMethod, P extends ApiPath<M>> =
  ApiSuccessOf<M, P> extends { result?: infer R } ? (NonNullable<R> extends ReadonlyArray<infer E> ? E : never) : never;
/** {@link ApiSuccessOf} for a POST route, e.g. `ApiSuccess<"/clashTests/getSelectedClashTreeData">["result"]`. */
export type ApiSuccess<P extends ApiPath<"post">> = ApiSuccessOf<"post", P>;
/** {@link ApiRowOf} for a POST route, e.g. `ApiRow<"/clashTests/getClashTestVersions">`. */
export type ApiRow<P extends ApiPath<"post">> = ApiRowOf<"post", P>;

type PathParamName<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | PathParamName<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;
/** A route path with no `:name` segment. */
type PlainPath<M extends ApiMethod> = Exclude<ApiPath<M>, `${string}:${string}`>;
/** A route path with `:name` segments, which a call fills from `config.pathParams`. */
type ParamPath<M extends ApiMethod> = Extract<ApiPath<M>, `${string}:${string}`>;
/** The value of each `:name` segment of a route path, as `api` sends it: one URL-encoded string each. */
export type ApiPathParams<P extends string> = { [Name in PathParamName<P>]: string };

/**
 * What a route call resolves with. Unless the config asks axios for another `responseType`, that is the route's own
 * declared response; a binary or text `responseType` decodes the same bytes as that type instead, and a config whose
 * `responseType` is not a literal says nothing about the body, so it resolves with `unknown`.
 */
type ApiReply<R, C> = "responseType" extends keyof C
  ? C extends { responseType?: "json" }
    ? R
    : C extends { responseType: "arraybuffer" }
      ? ArrayBuffer
      : C extends { responseType: "blob" }
        ? Blob
        : C extends { responseType: "text" }
          ? string
          : unknown
  : R;

/** The config of a call that resolves with the route's declared response. */
type JsonCallConfig = AxiosRequestConfig & { responseType?: "json" };

/** A route call's config: an axios config plus, for a path with `:name` segments, the values that fill them. */
type RouteCallConfig = AxiosRequestConfig & { pathParams?: Record<string, string> };

/** The query of a route URL built by `api.url`; an entry whose value is `undefined` is left out. */
export type ApiUrlParams = Record<string, string | number | boolean | undefined>;

export interface TypedAxiosInstance {
  post<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  get<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  put<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  delete<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  patch<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  /**
   * Calls a mounted server route by its method and path. The URL, the request body and the response body all come
   * from that route's own registration, so a call site declares no URL constant and no request or response type. A
   * path with `:name` segments takes their values in `config.pathParams`; `config.baseURL`, when set, replaces the
   * client's backend URL for that call.
   */
  api: {
    get<P extends PlainPath<"get">, C extends AxiosRequestConfig = {}>(
      path: P,
      config?: C & AxiosRequestConfig,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"get", P>, C>>>;
    get<P extends ParamPath<"get">, C extends AxiosRequestConfig & { pathParams: ApiPathParams<P> }>(
      path: P,
      config: C,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"get", P>, C>>>;
    post<P extends PlainPath<"post">, C extends AxiosRequestConfig = {}>(
      path: P,
      data: ApiRequest<"post", P>,
      config?: C & AxiosRequestConfig,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"post", P>, C>>>;
    post<P extends ParamPath<"post">, C extends AxiosRequestConfig & { pathParams: ApiPathParams<P> }>(
      path: P,
      data: ApiRequest<"post", P>,
      config: C,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"post", P>, C>>>;
    put<P extends PlainPath<"put">, C extends AxiosRequestConfig = {}>(
      path: P,
      data: ApiRequest<"put", P>,
      config?: C & AxiosRequestConfig,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"put", P>, C>>>;
    put<P extends ParamPath<"put">, C extends AxiosRequestConfig & { pathParams: ApiPathParams<P> }>(
      path: P,
      data: ApiRequest<"put", P>,
      config: C,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"put", P>, C>>>;
    patch<P extends PlainPath<"patch">, C extends AxiosRequestConfig = {}>(
      path: P,
      data: ApiRequest<"patch", P>,
      config?: C & AxiosRequestConfig,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"patch", P>, C>>>;
    patch<P extends ParamPath<"patch">, C extends AxiosRequestConfig & { pathParams: ApiPathParams<P> }>(
      path: P,
      data: ApiRequest<"patch", P>,
      config: C,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"patch", P>, C>>>;
    delete<P extends PlainPath<"delete">, C extends AxiosRequestConfig = {}>(
      path: P,
      config?: C & AxiosRequestConfig,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"delete", P>, C>>>;
    delete<P extends ParamPath<"delete">, C extends AxiosRequestConfig & { pathParams: ApiPathParams<P> }>(
      path: P,
      config: C,
    ): Promise<AxiosResponse<ApiReply<ApiResult<"delete", P>, C>>>;
    /**
     * The absolute URL of a GET route, for a consumer that is not an axios call (an `<img src>`, a download link,
     * `window.open`): built exactly as `api.get` builds its request — `:name` segments from `pathParams`, the query
     * from `params`.
     */
    url<P extends PlainPath<"get">>(path: P, options?: { params?: ApiUrlParams }): string;
    url<P extends ParamPath<"get">>(path: P, options: { pathParams: ApiPathParams<P>; params?: ApiUrlParams }): string;
  };
}

/**
 * A client's verbs, seen without their `NoAnyGuard` rest parameter: that guard cannot resolve for a response type
 * still generic over the route, and `routeApi` only ever names a route's declared response.
 */
type RouteVerbs = {
  get: <TResponse>(url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<TResponse>>;
  post: <TResponse>(url: string, data: unknown, config?: AxiosRequestConfig) => Promise<AxiosResponse<TResponse>>;
  put: <TResponse>(url: string, data: unknown, config?: AxiosRequestConfig) => Promise<AxiosResponse<TResponse>>;
  patch: <TResponse>(url: string, data: unknown, config?: AxiosRequestConfig) => Promise<AxiosResponse<TResponse>>;
  delete: <TResponse>(url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<TResponse>>;
};

/** Fills a route path's `:name` segments, each URL-encoded, from the values a call supplies. */
function fillPathParams(path: string, pathParams: Record<string, string> | undefined): string {
  if (!pathParams) return path;
  return path.replace(/:(\w+)/g, (_segment, name: string) => {
    const value = pathParams[name];
    if (value === undefined) throw new Error(`${path}: no value for :${name}`);
    return encodeURIComponent(value);
  });
}

/** A GET route's absolute URL on `backendUrl`: `:name` segments from `pathParams`, the query from `params`. */
function buildRouteUrl(
  backendUrl: string,
  path: string,
  options: { pathParams?: Record<string, string>; params?: ApiUrlParams } | undefined,
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(options?.params ?? {})) {
    if (value !== undefined) query.append(key, String(value));
  }
  const search = query.toString();
  return `${backendUrl}${fillPathParams(path, options?.pathParams)}${search ? `?${search}` : ""}`;
}

/**
 * `api.url` without a client, for a module the client itself depends on (importing the client there would be an
 * import cycle): the absolute URL of a GET route on the given backend.
 */
export function routeUrl<P extends PlainPath<"get">>(
  backendUrl: string,
  path: P,
  options?: { params?: ApiUrlParams },
): string;
export function routeUrl<P extends ParamPath<"get">>(
  backendUrl: string,
  path: P,
  options: { pathParams: ApiPathParams<P>; params?: ApiUrlParams },
): string;
export function routeUrl(
  backendUrl: string,
  path: string,
  options?: { pathParams?: Record<string, string>; params?: ApiUrlParams },
): string {
  return buildRouteUrl(backendUrl, path, options);
}

/**
 * Builds a client's `api` over that client's own verbs, so a client that wraps them (useAbortableApi adds an unmount
 * signal) applies the same wrapping to route calls, and every client turns a path into a URL one way.
 */
export function routeApi(getBackendUrl: () => string, verbs: RouteVerbs): TypedAxiosInstance["api"] {
  // `config.baseURL` names another backend for this one call (mobile reaches a portal's own server before sign-in).
  const url = (path: string, config: RouteCallConfig | undefined) =>
    `${config?.baseURL ?? getBackendUrl()}${fillPathParams(path, config?.pathParams)}`;
  const axiosConfig = (config: RouteCallConfig | undefined): AxiosRequestConfig | undefined => {
    if (!config) return undefined;
    const { pathParams: _pathParams, baseURL: _baseURL, ...rest } = config;
    return rest;
  };
  return {
    get: <TResponse>(path: string, config?: RouteCallConfig) =>
      verbs.get<TResponse>(url(path, config), axiosConfig(config)),
    post: <TResponse>(path: string, data: unknown, config?: RouteCallConfig) =>
      verbs.post<TResponse>(url(path, config), data, axiosConfig(config)),
    put: <TResponse>(path: string, data: unknown, config?: RouteCallConfig) =>
      verbs.put<TResponse>(url(path, config), data, axiosConfig(config)),
    patch: <TResponse>(path: string, data: unknown, config?: RouteCallConfig) =>
      verbs.patch<TResponse>(url(path, config), data, axiosConfig(config)),
    delete: <TResponse>(path: string, config?: RouteCallConfig) =>
      verbs.delete<TResponse>(url(path, config), axiosConfig(config)),
    url: (path: string, options?: { pathParams?: Record<string, string>; params?: ApiUrlParams }) =>
      buildRouteUrl(getBackendUrl(), path, options),
  };
}

/**
 * `api.post` with the route fixed: a function of the body alone, typed from the route — for handing the call to
 * something that makes it later, such as a grid's `remoteDataSource`. Each wrapper exposes it as `postTo`. The client
 * is a GETTER, read only when the call is made, so a module-scope `postTo(...)` is safe inside an import cycle.
 */
export function routeCaller<P extends ApiPostPath>(client: () => TypedAxiosInstance, path: P) {
  return (data: ApiRequest<"post", P>, config?: JsonCallConfig) => client().api.post(path, data, config);
}

// ============================================================================
// Shared Factory
// ============================================================================

const HTTP_STATUS = { UNAUTHORIZED: 401, FORBIDDEN: 403, TOO_MANY_REQUESTS: 429 } as const;
// Response types whose callers read `res.data` as bytes — `new Uint8Array(res.data)`,
// `URL.createObjectURL(res.data)`, `FileReader.readAsDataURL(res.data)`, pdf.js. Handing one of those
// the `{ status: false }` envelope the network branch synthesizes is worse than the rejection it was
// written against: the envelope is neither a Blob nor an ArrayBuffer, so it fails later and further
// from the cause. Every such call site already sits behind a `catch`, so rejecting is what they expect.
const BINARY_RESPONSE_TYPES = new Set(["arraybuffer", "blob", "stream"]);

/**
 * A rejection body is whatever the server sent, so it is checked before any member is read off it.
 * A non-object body (a string, a Blob, nothing at all) simply has none of them, which is how the
 * optional chains this replaces already behaved.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * `params` as the failure envelope declares it. Checked rather than asserted: `isRecord` alone only
 * proves the container is an object, so narrowing straight to this type would be an unsafe cast —
 * the values are what actually matter, since they get interpolated into a user-facing sentence.
 */
const isParams = (value: unknown): value is Record<string, string | number> =>
  isRecord(value) && Object.values(value).every((v) => typeof v === "string" || typeof v === "number");

const STATE_CHANGING_METHODS = new Set(["post", "put", "patch", "delete"]);
const CSRF_ERROR_CODES: number[] = [
  errorCodes.CSRF_TOKEN_MISMATCH,
  errorCodes.CSRF_TOKEN_INVALID,
  errorCodes.CSRF_TOKEN_MISSING,
];
let cachedCsrfToken: string | null = null;
let csrfFetchInFlight: Promise<string | null> | null = null;

const CSRF_FETCH_SLOW_THRESHOLD_MS = 2500;

/**
 * This wrapper's own upload-progress convention. The request interceptor below REPLACES
 * `onUploadProgress` and calls whatever the caller put there back as `(percentComplete,
 * bytesLoaded)` — two numbers — never with axios's single `AxiosProgressEvent`. `AxiosRequestConfig`
 * can only declare axios's own signature, so the config callback is read at this one instead.
 */
type WrapperUploadProgressCallback = (percentComplete: number, bytesLoaded: number) => void;

async function fetchCsrfToken(instance: AxiosInstance, csrfUrl: string): Promise<string | null> {
  if (csrfFetchInFlight) return csrfFetchInFlight;
  csrfFetchInFlight = (async () => {
    try {
      // `| undefined`: axios only fills `data` when a body came back and parsed. A 204 or a body
      // the transform could not parse leaves it unset, which is exactly what the guard below reads.
      const res = await instance.get<{ csrfToken: string | null } | undefined>(csrfUrl, {
        withCredentials: true,
      });
      cachedCsrfToken = res.data?.csrfToken ?? null;
      if (!cachedCsrfToken) {
        Sentry.captureMessage("csrf_token_empty_body", { level: "warning", extra: { url: csrfUrl } });
      }
      return cachedCsrfToken;
    } catch (error) {
      Sentry.captureMessage("csrf_token_fetch_failed", {
        extra: { url: csrfUrl, error: error instanceof Error ? error.message : String(error) },
      });
      return null;
    } finally {
      csrfFetchInFlight = null;
    }
  })();
  return csrfFetchInFlight;
}

export function clearCsrfToken() {
  cachedCsrfToken = null;
}

// 499 (nginx "client closed request") and the >= 502 gateway band (502/503/504, Cloudflare 520-527,
// proxy 598/599, …) are statuses the app never emits — every app failure lives at <= 500 (verified by
// scanning the backend: the only in-band app response is cad2pdf's staging-only converter 502, and a
// converter failure IS a gateway error, so the message fits it too). So a response in this band came
// from the ingress / LB / proxy / WAF in front of the app. The STATUS decides, deliberately not the
// body — an infra error page that happens to mimic the `{ status: false }` envelope still can't slip past.
export const isInfraFailureStatus = (status: number): boolean => status === 499 || status >= 502;

function serverUnavailableEnvelopeData() {
  return {
    status: false as const,
    errorCode: errorCodes.SERVER_UNAVAILABLE,
  };
}

export function createAxiosWrapper(adapter: PlatformAdapter, instance: AxiosInstance = axios) {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const returnedConfig: InternalAxiosRequestConfig = {
        ...config,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (config.onUploadProgress && progressEvent.total) {
            const percentage = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see `WrapperUploadProgressCallback`: the two signatures are unrelated, and this wrapper is what defines the one the call sites are written against (e.g. FileUpload.tsx:1107)
            (config.onUploadProgress as unknown as WrapperUploadProgressCallback)(percentage, progressEvent.loaded);
          }
        },
      };
      const useCookieAuth = adapter.useCookieAuth ?? true;
      returnedConfig.withCredentials = useCookieAuth;

      if (adapter.getRequestHeaders) {
        const headers = await adapter.getRequestHeaders();
        Object.assign(returnedConfig.headers, headers);
      }

      const method = (returnedConfig.method || "get").toLowerCase();
      const url = returnedConfig.url || "";
      // The CSRF double-submit only exists for cookie sessions; Bearer-auth platforms
      // send no cookies, so the server skips CSRF and the token fetch is dead weight.
      if (useCookieAuth && STATE_CHANGING_METHODS.has(method) && !url.endsWith("/csrf-token")) {
        const csrfUrl = `${adapter.getBackendUrl()}/csrf-token`;
        const csrfAwaitStart = performance.now();
        const token = cachedCsrfToken ?? (await fetchCsrfToken(instance, csrfUrl));
        const csrfAwaitElapsedMs = Math.round(performance.now() - csrfAwaitStart);
        if (csrfAwaitElapsedMs >= CSRF_FETCH_SLOW_THRESHOLD_MS) {
          Sentry.captureMessage("csrf_token_await_slow", {
            level: "warning",
            extra: {
              url,
              method,
              elapsedMs: csrfAwaitElapsedMs,
              thresholdMs: CSRF_FETCH_SLOW_THRESHOLD_MS,
            },
          });
        }
        if (token) {
          returnedConfig.headers.set("X-CSRF-Token", token);
        } else if (adapter.isAuthenticated ? adapter.isAuthenticated() : true) {
          Sentry.captureMessage("csrf_header_missing_on_request", {
            level: "warning",
            extra: {
              url,
              method,
              resolvedTokenPresent: Boolean(token),
              cachedTokenPresent: Boolean(cachedCsrfToken),
              fetchInFlight: Boolean(csrfFetchInFlight),
              pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
            },
          });
          const err = new Error("csrf_header_missing");
          globalThis.dispatchEvent(
            new ErrorEvent("error", { error: err, message: err.message, filename: "axiosShared.ts" }),
          );
          return Promise.reject(err);
        }
      }

      return returnedConfig;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      adapter.onBackendReachability?.(true);
      const data: unknown = response.data;
      if (isRecord(data) && typeof data.errorCode === "number" && CSRF_ERROR_CODES.includes(data.errorCode)) {
        // The code alone is enough — the three CSRF sentences live in the mapper (each embeds its own
        // number), so the platform resolves and translates them.
        if (adapter.actions.showToast) {
          adapter.dispatch(
            adapter.actions.showToast({
              status: false,
              errorCode: data.errorCode,
              ...(isParams(data.params) ? { params: data.params } : {}),
            }),
          );
        }
      }
      return response;
    },
    // Axios types its rejection handler as `(error: any)`. The rejection is an `AxiosError` for
    // anything that reached the transport, and a plain `Error` for a request-interceptor rejection
    // (the csrf branch above) — which is why every read below was already optional-chained.
    // `axios.isAxiosError` is a real runtime check (it reads the flag axios sets), so the error
    // comes back typed instead of asserted; the `<unknown>` type argument stops `response.data`
    // defaulting back to `any`, since the body is whatever the server sent.
    async (error: unknown) => {
      const axiosError = axios.isAxiosError<unknown>(error) ? error : undefined;
      const response = axiosError?.response;
      if (axiosError && axiosError.code !== "ERR_CANCELED") {
        adapter.onBackendReachability?.(response ? !isInfraFailureStatus(response.status) : false);
      }
      const responseData: unknown = response?.data;
      const responseBody = isRecord(responseData) ? responseData : undefined;
      // `redirectToLogin` takes a `string`; every server error envelope carries one here, and a body
      // that does not is left without a message rather than handed on as something unrenderable.
      const rawErrorMessage: unknown = responseBody?.errorMessage;
      const responseErrorMessage = typeof rawErrorMessage === "string" ? rawErrorMessage : undefined;
      // A transient network failure (axios ERR_NETWORK) means the request never reached the server or no
      // response came back — offline, dropped connection, aborted socket, DNS/CORS/mixed-content. Axios
      // rejects these with no `error.response`; left to propagate they become `AxiosError: Network Error`
      // unhandled rejections at every uncaught call site. Normalized to the same `{ status: false }`
      // envelope the app already uses for failed operations (mirrors the 429 branch below).
      // ⚠️ Placed FIRST: a network error has no `error.response`, so it must never reach the 401 logout
      // path. Genuine cancellations (ERR_CANCELED) are intentionally left to reject — callers that abort
      // expect the rejection, and so do the binary callers carved out below.
      if (axiosError?.code === "ERR_NETWORK") {
        const responseType = axiosError.config?.responseType;
        if (responseType && BINARY_RESPONSE_TYPES.has(responseType)) return Promise.reject(error);
        return {
          ...(response ?? {}),
          // There is no response to spread, so without these the synthesized object has no `status`
          // at all and every caller that branches on `res.status` reads `undefined`. 0 is what a
          // browser reports for a transport that never completed.
          status: response?.status ?? 0,
          statusText: response?.statusText ?? "Network Error",
          data: {
            status: false,
            errorCode: errorCodes.NETWORK_ERROR,
          },
        };
      }

      const status = String(response?.status);

      if (status === String(HTTP_STATUS.UNAUTHORIZED)) {
        const currentPath = adapter.navigation.getCurrentPath();
        // An external-share recipient (CP-283, /share/:token/...) must NEVER be bounced to sign-in:
        // they have no account to sign in with, and the logout side-effects below would also wipe the
        // session of a LOGGED-IN user who merely opened a share link. Every share-mode network call is
        // supposed to be branched onto public routes — this is the backstop that turns any missed
        // branch into a broken feature instead of a lost recipient. Prefix match, because the exact
        // -match list below can never cover a tokenised path.
        if (currentPath.startsWith("/share/")) {
          return Promise.reject(error);
        }
        if (!adapter.excludedPathsFor401?.includes(currentPath)) {
          await adapter.storage.removeItem("isLogedIn");
          await adapter.storage.removeItem("jwtToken");
          adapter.dispatch(adapter.actions.setLoggedOut());
          adapter.navigation.redirectToLogin({
            redirectUrl: currentPath,
            errorMessage: responseErrorMessage,
          });
        }
      }

      if (status === String(HTTP_STATUS.FORBIDDEN) && responseBody?.errorCode === errorCodes.LICENCE_EXPIRED) {
        console.log("licence expired");
        if (adapter.actions.setLicenceInvalid) {
          adapter.dispatch(adapter.actions.setLicenceInvalid());
        }
      }

      if (status === String(HTTP_STATUS.TOO_MANY_REQUESTS)) {
        return {
          ...(response ?? {}),
          data: {
            status: false,
            // Forward the server's OWN code. This used to be hardcoded to ACCOUNT_RATE_LIMIT_EXCEEDED
            // (7023, "Too many verification codes requested."), which silently rewrote all five 429
            // senders — so a rate-limited share link rendered a verification-code message and its
            // `params` were dropped. The constant stays only as the fallback for a body with no code.
            errorCode:
              typeof responseBody?.errorCode === "number"
                ? responseBody.errorCode
                : errorCodes.ACCOUNT_RATE_LIMIT_EXCEEDED,
            ...(isParams(responseBody?.params) ? { params: responseBody.params } : {}),
            retryAfter: responseBody?.retryAfter,
          },
        };
      }

      // Infra failure (see `isInfraFailureStatus`): a gateway-band status, normalized to one message so
      // the SPA shows "contact your administrator" instead of rejecting on nginx's HTML/text error page.
      // The auth/rate-limit branches above ran first (all < 502, so no overlap). Binary callers
      // (downloads) can't consume the envelope, so they keep rejecting into their own `catch`.
      if (response && isInfraFailureStatus(response.status)) {
        const responseType = axiosError.config?.responseType;
        if (responseType && BINARY_RESPONSE_TYPES.has(responseType)) return Promise.reject(error);
        const envelope = serverUnavailableEnvelopeData();
        if (adapter.actions.showToast) {
          adapter.dispatch(adapter.actions.showToast(envelope));
        }
        return {
          ...response,
          data: envelope,
        };
      }

      return Promise.reject(error);
    },
  );

  const verbs: Omit<TypedAxiosInstance, "api"> = {
    post: (url, data, config) => instance.post(url, data, config),
    get: (url, config) => instance.get(url, config),
    put: (url, data, config) => instance.put(url, data, config),
    delete: (url, config) => instance.delete(url, config),
    patch: (url, data, config) => instance.patch(url, data, config),
  };
  const typedAxios: TypedAxiosInstance = { ...verbs, api: routeApi(adapter.getBackendUrl, verbs) };

  return { instance, typedAxios };
}
